import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Briefcase,
  Kanban,
  Globe,
  ShieldCheck,
  Building2,
  FolderOpen,
  Sparkles,
  Bot,
  Mail,
  Users,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Receipt,
  FileText,
  Wrench,
  Ticket,
  Menu,
  X,
  Wand2,
  User,
  AlertTriangle,
  Tags,
  Wallet,
  ShoppingCart,
  Factory,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { NotificationBell } from "@/components/NotificationBell";

const NAV_GROUPS = [
  {
    title: "Workspace",
    items: [{ to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testId: "nav-dashboard" }],
  },
  {
    title: "Bids",
    items: [
      { to: "/bids", label: "My Bids", icon: Briefcase, testId: "nav-bids" },
      { to: "/bid-wizard", label: "Bid Wizard", icon: Wand2, testId: "nav-bid-wizard" },
      { to: "/pipeline", label: "Pipeline", icon: Kanban, testId: "nav-pipeline" },
      { to: "/feeds/egp", label: "e-GP Uganda", icon: Globe, testId: "nav-egp" },
      { to: "/feeds/ungm", label: "UNGM", icon: Globe, testId: "nav-ungm" },
      { to: "/feeds/ungm-eoi", label: "UNGM · My EOI", icon: ShieldCheck, testId: "nav-ungm-eoi" },
      { to: "/feeds/unhcr", label: "UNHCR", icon: ShieldCheck, testId: "nav-unhcr" },
      { to: "/feeds/nsd", label: "PAU / NSD", icon: Building2, testId: "nav-nsd" },
      { to: "/proposals", label: "AI Proposals", icon: Sparkles, testId: "nav-proposals" },
    ],
  },
  {
    title: "DMS",
    items: [
      { to: "/dms/quotations", label: "Quotations", icon: Receipt, testId: "nav-quotations" },
      { to: "/dms/invoices", label: "Invoices", icon: FileText, testId: "nav-invoices" },
      { to: "/dms/job-cards", label: "Job Cards", icon: Wrench, testId: "nav-jobcards" },
      { to: "/dms/sdns", label: "Service Delivery Notes", icon: ClipboardList, testId: "nav-sdns" },
      { to: "/dms/lpos", label: "LPOs (Purchasing)", icon: ShoppingCart, testId: "nav-lpos" },
      { to: "/suppliers", label: "Suppliers", icon: Factory, testId: "nav-suppliers" },
      { to: "/price-list", label: "Price List", icon: Tags, testId: "nav-price-list" },
    ],
  },
  {
    title: "Service",
    items: [
      { to: "/tickets", label: "Tickets", icon: Ticket, testId: "nav-tickets" },
    ],
  },
  {
    title: "Library",
    items: [
      { to: "/documents", label: "Document Vault", icon: FolderOpen, testId: "nav-docs" },
      { to: "/emails", label: "Emails", icon: Mail, testId: "nav-emails" },
      { to: "/assistant", label: "AI Assistant", icon: Bot, testId: "nav-assistant" },
    ],
  },
];

const ADMIN_ITEMS = [
  { to: "/team", label: "Team", icon: Users, testId: "nav-team" },
  { to: "/email-templates", label: "Email Templates", icon: Mail, testId: "nav-email-templates" },
  { to: "/company", label: "Company Settings", icon: Settings, testId: "nav-company" },
];

const MOBILE_NAV = [
  { to: "/", icon: LayoutDashboard, label: "Home", end: true },
  { to: "/bids", icon: Briefcase, label: "Bids" },
  { to: "/dms/quotations", icon: Receipt, label: "Docs" },
  { to: "/tickets", icon: Ticket, label: "Tickets" },
  { to: "/assistant", icon: Bot, label: "AI" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("bt_sidebar_collapsed") === "1"
  );
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("bt_sidebar_collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  const initials = (user?.full_name || user?.email || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const sidebarWidth = collapsed ? 68 : 232;

  const Logo = collapsed ? (
    <div className="flex justify-center py-5">
      <img
        src="/brand/logo-transparent.png"
        alt="Jero-One"
        className="w-9 h-9 object-contain"
      />
    </div>
  ) : (
    <div className="flex items-center gap-2.5 px-5 pt-5 pb-3">
      <img
        src="/brand/logo-transparent.png"
        alt="Jero-One"
        className="w-10 h-10 object-contain flex-shrink-0"
      />
      <div className="leading-tight">
        <div className="text-white text-[14px] font-bold tracking-tight">Jero-One</div>
        <div className="text-white/55 text-[10px] uppercase tracking-wider">
          Workspace
        </div>
      </div>
    </div>
  );

  const renderNavItem = (item) => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.end}
      data-testid={item.testId}
      onClick={() => setMobileOpen(false)}
      title={collapsed ? item.label : ""}
      className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
    >
      <item.icon size={16} strokeWidth={2} />
      <span className="nav-link-label">{item.label}</span>
    </NavLink>
  );

  // Customer role sees a restricted sidebar (only Tickets + Profile)
  const isCustomer = user?.role === "customer";
  const CUSTOMER_NAV = [
    {
      title: "Support",
      items: [
        { to: "/tickets", label: "My Tickets", icon: Ticket, testId: "nav-tickets" },
        { to: "/profile", label: "My Profile", icon: User, testId: "nav-profile" },
      ],
    },
  ];

  const sidebarContent = (
    <>
      {Logo}

      <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-1">
        {(isCustomer ? CUSTOMER_NAV : NAV_GROUPS).map((g) => (
          <div key={g.title}>
            <div className="nav-section">{g.title}</div>
            {g.items.map(renderNavItem)}
          </div>
        ))}
        {!isCustomer && (
          <div>
            <div className="nav-section">Finance</div>
            {renderNavItem({ to: "/hr", label: (user?.role === "admin" || user?.hr_access) ? "HR & Payroll" : "My Payslips", icon: Wallet, testId: "nav-hr" })}
          </div>
        )}
        {!isCustomer && user?.role === "admin" && (
          <div>
            <div className="nav-section">Admin</div>
            {ADMIN_ITEMS.map(renderNavItem)}
          </div>
        )}
      </nav>

      <div className="px-3 pb-3">
        <button
          data-testid="profile-btn"
          onClick={() => {
            nav("/profile");
            setMobileOpen(false);
          }}
          className={`flex items-center gap-2.5 w-full px-2.5 py-2.5 rounded-lg hover:bg-white/10 transition ${
            collapsed ? "justify-center" : ""
          }`}
        >
          <div className="avatar avatar-sm">{initials}</div>
          {!collapsed && (
            <div className="flex-1 text-left min-w-0">
              <div className="text-white text-[12px] font-semibold truncate">
                {user?.full_name || user?.email}
              </div>
              <div className="text-white/55 text-[10px] uppercase tracking-wider">
                {user?.role}
              </div>
            </div>
          )}
        </button>
        <button
          data-testid="logout-btn"
          onClick={logout}
          className={`nav-link mt-1 ${collapsed ? "justify-center" : ""}`}
          title={collapsed ? "Sign out" : ""}
        >
          <LogOut size={16} />
          <span className="nav-link-label">Sign out</span>
        </button>

        {/* Collapse toggle (desktop) */}
        <button
          data-testid="collapse-btn"
          onClick={() => setCollapsed((c) => !c)}
          className={`hidden lg:flex items-center gap-2 mt-3 px-3 py-1.5 rounded-lg text-white/55 hover:bg-white/10 hover:text-white text-[11px] font-semibold uppercase tracking-wider w-full ${
            collapsed ? "justify-center" : ""
          }`}
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Desktop sidebar */}
      <aside
        className={`sidebar-desktop sidebar ${collapsed ? "collapsed" : ""} flex flex-col flex-shrink-0`}
        style={{ width: sidebarWidth, transition: "width .2s ease" }}
        data-testid="sidebar"
      >
        {sidebarContent}
      </aside>

      {/* Mobile sidebar (overlay) */}
      {mobileOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-black/40 z-50"
            onClick={() => setMobileOpen(false)}
          />
          <aside
            className="lg:hidden fixed left-0 top-0 bottom-0 w-64 sidebar flex flex-col z-50 animate-slide-in"
            data-testid="mobile-sidebar"
          >
            <div className="flex items-center justify-between pr-3">
              {Logo}
              <button onClick={() => setMobileOpen(false)} className="text-white p-2">
                <X size={20} />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-1">
              {(isCustomer ? CUSTOMER_NAV : NAV_GROUPS).map((g) => (
                <div key={g.title}>
                  <div className="nav-section">{g.title}</div>
                  {g.items.map(renderNavItem)}
                </div>
              ))}
              {!isCustomer && user?.role === "admin" && (
                <div>
                  <div className="nav-section">Admin</div>
                  {ADMIN_ITEMS.map(renderNavItem)}
                </div>
              )}
            </nav>
            <div className="px-3 pb-4">
              <button
                onClick={() => {
                  logout();
                  setMobileOpen(false);
                }}
                className="nav-link"
              >
                <LogOut size={16} /> <span className="nav-link-label">Sign out</span>
              </button>
            </div>
          </aside>
        </>
      )}

      {/* Main */}
      <main className="flex-1 overflow-y-auto" data-testid="main-content">
        {!isCustomer && <NotificationBell />}
        {/* Mobile header */}
        <div className="lg:hidden sticky top-0 bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between z-30">
          <button
            data-testid="open-mobile-nav"
            onClick={() => setMobileOpen(true)}
            className="p-1"
          >
            <Menu size={20} className="text-slate-700" />
          </button>
          <div className="flex items-center gap-2">
            <img
              src="/brand/logo-transparent.png"
              alt="Jero-One"
              className="w-8 h-8 object-contain"
              style={{ filter: "brightness(0) saturate(100%) invert(22%) sepia(78%) saturate(587%) hue-rotate(116deg) brightness(95%) contrast(94%)" }}
            />
            <span className="font-bold text-sm">Jero-One Workspace</span>
          </div>
          <div className="avatar avatar-sm">{initials}</div>
        </div>

        <div className="px-4 lg:px-8 py-6 lg:py-8 pb-24 lg:pb-10 max-w-[1480px] mx-auto">
          {user?.must_change_password && (
            <div
              data-testid="pwd-expired-banner"
              className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex items-start gap-3"
            >
              <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center shrink-0">
                <AlertTriangle size={15} className="text-red-700" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm text-red-800">Password change required</div>
                <div className="text-xs text-red-700/90 mt-0.5">
                  Your password has expired (60-day policy) or is set to require an update on first login.
                  Please change it now to keep your account secure.
                </div>
              </div>
              <button
                onClick={() => nav("/profile")}
                className="btn btn-primary text-xs shrink-0"
                data-testid="pwd-expired-goto"
              >
                Update now
              </button>
            </div>
          )}
          {children}
        </div>
      </main>

      {/* Mobile bottom nav */}
      <div className="mobile-nav" data-testid="mobile-nav">
        {MOBILE_NAV.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            className={({ isActive }) => (isActive ? "active" : "")}
            data-testid={`mnav-${it.label.toLowerCase()}`}
          >
            <span style={{ display: "inline-flex", alignItems: "center" }}>
              <it.icon size={18} />
            </span>
            <span>{it.label}</span>
          </NavLink>
        ))}
      </div>
    </div>
  );
}
